/**
 * Deterministic pseudo-random numbers.
 *
 * Everything in `src/shared` must be reproducible: every client builds the level
 * from the same seed and must get a byte-identical course. Nothing here (and
 * nothing that calls it) may use `Math.random`.
 */

/** A seeded generator returning floats in `[0, 1)`. */
export type Rng = () => number;

/**
 * Mulberry32 — a small, fast, well-distributed 32-bit PRNG.
 *
 * @param seed any 32-bit integer (coerced with `>>> 0`)
 * @returns a generator producing floats in `[0, 1)`
 */
export function mulberry32(seed: number): Rng {
  let a = seed >>> 0;
  return function next(): number {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Uniform float in `[min, max)`. */
export function randRange(rng: Rng, min: number, max: number): number {
  return min + rng() * (max - min);
}

/** Uniform integer in `[min, max]` — both ends inclusive. */
export function randInt(rng: Rng, min: number, max: number): number {
  const lo = Math.ceil(min);
  const hi = Math.floor(max);
  if (hi < lo) return lo;
  return lo + Math.floor(rng() * (hi - lo + 1));
}

/**
 * Pick one entry from `[value, weight]` pairs, proportional to weight.
 *
 * Non-positive weights are ignored. Throws only if the list is empty or every
 * weight is non-positive — a level-generation bug worth failing loudly on.
 */
export function pickWeighted<T>(rng: Rng, items: Array<[T, number]>): T {
  let total = 0;
  for (const entry of items) {
    if (entry[1] > 0) total += entry[1];
  }
  if (total <= 0) throw new Error('pickWeighted: no entry with a positive weight');

  let roll = rng() * total;
  for (const entry of items) {
    const w = entry[1];
    if (w <= 0) continue;
    roll -= w;
    if (roll <= 0) return entry[0];
  }
  // Float drift on the last step — fall back to the final positive-weight entry.
  for (let i = items.length - 1; i >= 0; i--) {
    const entry = items[i];
    if (entry !== undefined && entry[1] > 0) return entry[0];
  }
  throw new Error('pickWeighted: no entry with a positive weight');
}
